import { CalendarPlus, MessageCircle, MapPin, Clock, User, Scissors, BadgeEuro } from "lucide-react";

const icsStamp = (iso) => (iso || "").replace(/[-:]/g, "").slice(0, 15);
const icsEsc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/[,;]/g, (m) => `\\${m}`);

function downloadIcs({ id, start, end, title, location, description }) {
  const now = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Chatity//Booking//EN", "BEGIN:VEVENT",
    `UID:${id}@chatity.io`, `DTSTAMP:${now}`, `DTSTART:${icsStamp(start)}`, `DTEND:${icsStamp(end)}`,
    `SUMMARY:${icsEsc(title)}`, `LOCATION:${icsEsc(location)}`, `DESCRIPTION:${icsEsc(description)}`,
    "BEGIN:VALARM", "TRIGGER:-PT2H", "ACTION:DISPLAY", `DESCRIPTION:${icsEsc(title)}`, "END:VALARM",
    "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = "visit.ics"; document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
}

// done = /public/booking/{salon}/book response (+ lead:true for requests)
export default function BookingSuccess({ done, tf, lang, accent, salonName, address, waTarget, currency, phoneLabel, onAgain }) {
  const start = done.start_at || "";
  const end = done.end_at || start;
  const dateLabel = start ? new Date(start).toLocaleDateString(lang || "en", { weekday: "long", day: "numeric", month: "long" }) : "";
  const timeLabel = start.slice(11, 16);
  const title = `${done.service || ""} — ${salonName || ""}`.trim();
  const desc = [done.staff_name ? `${tf("ms.book.master", "Мастер")}: ${done.staff_name}` : "", phoneLabel].filter(Boolean).join("\n");
  const gcal = start ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${icsStamp(start)}/${icsStamp(end)}&details=${encodeURIComponent(desc)}&location=${encodeURIComponent(address || "")}` : null;
  const waText = tf("ms.book.waHello", "Здравствуйте! Я записан(а) на {service} {date} в {time}.")
    .replace("{service}", done.service || "").replace("{date}", dateLabel).replace("{time}", timeLabel);
  const waHref = waTarget ? `https://wa.me/${waTarget.replace(/\D/g, "")}?text=${encodeURIComponent(waText)}` : null;
  const Row = ({ Icon, children, testId }) => (
    <div className="flex items-start gap-2.5 text-sm text-gray-800" data-testid={testId}>
      <Icon size={16} className="mt-0.5 shrink-0" style={{ color: accent }} /><span className="min-w-0 break-words">{children}</span>
    </div>
  );

  return (
    <section id="ms-booking" className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-5 md:p-6 mb-4" data-testid="minisite-booking-success">
      <div className="text-center mb-4">
        <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl mb-2" style={{ backgroundColor: accent }}>✓</div>
        <h2 className="text-lg md:text-xl font-bold">
          {done.lead ? tf("ms.book.leadSent", "Заявка отправлена! Мы скоро свяжемся с вами") : tf("ms.book.success", "Вы записаны!")}
        </h2>
        {done.whatsapp_sent && (
          <p className="text-sm text-green-600 mt-1 font-medium" data-testid="minisite-booking-wa">
            💬 {tf("ms.book.successWa", "Подтверждение отправлено в WhatsApp")}
          </p>
        )}
      </div>

      {!done.lead && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2.5 mb-4" data-testid="minisite-success-details">
          <Row Icon={Clock} testId="minisite-success-when"><span className="capitalize">{dateLabel}</span> · <b>{timeLabel}</b>{done.duration_min ? ` · ${done.duration_min} ${tf("ms.book.minutes", "мин")}` : ""}</Row>
          <Row Icon={Scissors} testId="minisite-success-service">{done.service}</Row>
          {done.staff_name && <Row Icon={User} testId="minisite-success-master">{done.staff_name}</Row>}
          {address && <Row Icon={MapPin} testId="minisite-success-address">{done.branch_name ? `${done.branch_name} · ` : ""}{address}</Row>}
          {done.price != null && <Row Icon={BadgeEuro} testId="minisite-success-price">{currency}{done.price}</Row>}
        </div>
      )}
      {phoneLabel && <p className="text-xs text-gray-500 text-center mb-4" data-testid="minisite-success-phone">{phoneLabel}</p>}

      <div className="grid gap-2 sm:grid-cols-2">
        {!done.lead && start && (
          <button type="button" data-testid="minisite-success-ics"
            onClick={() => downloadIcs({ id: done.booking_id, start, end, title, location: address, description: desc })}
            className="flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white hover:opacity-85 transition-opacity"
            style={{ backgroundColor: accent }}>
            <CalendarPlus size={16} /> {tf("ms.book.addToCalendar", "Добавить в календарь")}
          </button>
        )}
        {!done.lead && gcal && (
          <a href={gcal} target="_blank" rel="noopener noreferrer" data-testid="minisite-success-gcal"
            className="flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold border hover:bg-gray-50 transition-colors"
            style={{ borderColor: accent, color: accent }}>
            <CalendarPlus size={16} /> {tf("ms.book.googleCal", "Google Календарь")}
          </a>
        )}
        {waHref && (
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="minisite-success-whatsapp"
            className="flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold border border-green-500 text-green-700 hover:bg-green-50 transition-colors sm:col-span-2">
            <MessageCircle size={16} /> {tf("ms.book.writeWa", "Написать в WhatsApp")}
          </a>
        )}
      </div>
      <button type="button" onClick={onAgain} className="mt-4 w-full text-sm font-semibold text-gray-500 hover:text-gray-800" data-testid="minisite-booking-again">
        {tf("ms.book.another", "Записаться ещё")}
      </button>
    </section>
  );
}
